/**
 * NF-1 — HQ Admin Subscriptions Management Page
 *
 * Read-only view of all affiliate and individual subscriptions.
 * Queries D1 directly — no Stripe API calls.
 * Supports filtering by status, type, and search.
 */
import { StatCard } from '@/components/custom/StatCard';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminApi } from '@/hooks/useAdminApi';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  CheckCircle,
  Clock,
  Loader2,
  PauseCircle,
  Search,
  User,
  XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';

interface Subscription {
  id: string;
  name: string | null;
  email: string | null;
  type: 'affiliate' | 'individual';
  plan_name: string;
  status: string;
  stripe_customer_id: string | null;
  is_paused: number;
  pause_resumes_at: string | null;
  created_at: string;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function StatusBadge({ status, isPaused }: { status: string; isPaused: number }) {
  if (isPaused) {
    return (
      <Badge variant="outline" className="border-yellow-500 text-yellow-500">
        <PauseCircle className="mr-1 h-3 w-3" />
        Paused
      </Badge>
    );
  }

  switch (status) {
    case 'active':
      return (
        <Badge variant="outline" className="border-green-500 text-green-500">
          <CheckCircle className="mr-1 h-3 w-3" />
          Active
        </Badge>
      );
    case 'past_due':
      return (
        <Badge variant="outline" className="border-orange-500 text-orange-500">
          <AlertTriangle className="mr-1 h-3 w-3" />
          Past Due
        </Badge>
      );
    case 'cancelled':
      return (
        <Badge variant="outline" className="border-red-500 text-red-500">
          <XCircle className="mr-1 h-3 w-3" />
          Cancelled
        </Badge>
      );
    case 'pending':
      return (
        <Badge variant="outline" className="border-muted-foreground text-muted-foreground">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function TypeBadge({ type }: { type: 'affiliate' | 'individual' }) {
  if (type === 'affiliate') {
    return (
      <Badge variant="secondary">
        <Building2 className="mr-1 h-3 w-3" />
        Gym
      </Badge>
    );
  }
  return (
    <Badge variant="secondary">
      <User className="mr-1 h-3 w-3" />
      Individual
    </Badge>
  );
}

export default function AdminSubscriptions() {
  const adminApi = useAdminApi();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-subscriptions', statusFilter, typeFilter, searchQuery],
    queryFn: async () => {
      const params: {
        status?: string;
        search?: string;
        type?: string;
        limit?: number;
      } = { limit: 200 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const response = await adminApi.getSubscriptions(params);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const subscriptions = data?.subscriptions ?? [];

  const stats = useMemo(() => {
    // Stats are always computed from the full fetched set
    const active = subscriptions.filter((s) => s.status === 'active' && !s.is_paused).length;
    const pastDue = subscriptions.filter((s) => s.status === 'past_due').length;
    const paused = subscriptions.filter((s) => s.is_paused).length;
    const cancelled = subscriptions.filter((s) => s.status === 'cancelled').length;
    return { active, pastDue, paused, cancelled };
  }, [subscriptions]);

  return (
    <>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">Subscriptions</h1>
        <p className="mt-1 text-muted-foreground">
          All affiliate and individual subscription records from D1.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard title="Active" value={stats.active} icon={CheckCircle} />
        <StatCard title="Past Due" value={stats.pastDue} icon={AlertTriangle} />
        <StatCard title="Paused" value={stats.paused} icon={PauseCircle} />
        <StatCard title="Cancelled" value={stats.cancelled} icon={XCircle} />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="past_due">Past Due</option>
          <option value="paused">Paused</option>
          <option value="cancelled">Cancelled</option>
          <option value="pending">Pending</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All Types</option>
          <option value="affiliate">Gym</option>
          <option value="individual">Individual</option>
        </select>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Subscription Records
            {data && (
              <Badge variant="secondary" className="ml-2">
                {data.total}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-12 text-destructive">
              Failed to load subscriptions.
            </div>
          )}

          {data && subscriptions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Building2 className="h-8 w-8 text-muted-foreground mb-4" />
              <p className="font-semibold">No subscriptions found</p>
              <p className="text-sm text-muted-foreground max-w-md">
                Adjust your filters or check back when subscriptions have been created.
              </p>
            </div>
          )}

          {data && subscriptions.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.map((sub: Subscription) => (
                    <TableRow key={`${sub.type}-${sub.id}`}>
                      <TableCell className="font-medium">{sub.name || 'Unknown'}</TableCell>
                      <TableCell className="text-muted-foreground">{sub.email || '-'}</TableCell>
                      <TableCell>
                        <TypeBadge type={sub.type} />
                      </TableCell>
                      <TableCell>{sub.plan_name}</TableCell>
                      <TableCell>
                        <StatusBadge status={sub.status} isPaused={sub.is_paused} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(sub.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
